import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { WorkDayResponse } from '@/hooks/workDayTrucksConfirmed/useWorkDayTrucksConfirmed'
import { DEFAULT_TIMEZONE, HOUSTON_BRANCH_ID } from '@/types/constants'
import { toDateString, todayInTimezone } from '@/utils/dateUtils'

interface ModalRentalInfo {
  type: 'add' | 'remove'
  currentIdTruck: string
  truckName: string
}

interface ModalStatusInfo {
  type: 'create' | 'edit' | 'delete'
  currentIdTrucks: string[]
  truckName: string
  currentNote: string
  currentUnavailableTruckIds?: string[]
  isRental?: boolean
}

interface ModalAssignMoverInfo {
  truckId: string
  position: number
  target: 'truck' | 'onCall'
}

interface ModalMoveToTruckInfo {
  operationId: string
  customerNounce: string
  currentTruckId: string
  customerName?: string
  /** Move start time (ISO string), passed through for conflict checks. */
  start?: string
  /** Move duration in hours. */
  duration?: number
  /** BookingEnvelope used by the overlap check (jobStart, driveTimes, etc). */
  bookingEnvelope?: {
    jobStart?: string | Date | null
    jobDuration?: number
    startingDriveTime?: number
    endingDriveTime?: number
  } | null
}

interface MoveManagerStore {
  date: Date
  setDate: (date: Date) => void
  currentDay: string
  /**
   * True once the user picked a day themselves. Until then the calendar keeps
   * following "today", so it can be re-pointed at the branch's today once the
   * branch (and therefore its timezone) is known.
   */
  dateSelectedByUser: boolean
  /** Re-point an unattended calendar at today on the branch's clock. */
  alignDateToTimezone: (timezone: string) => void

  currentMainBranch: string
  setCurrentMainBranch: (branch: string) => void

  modalRentalOpened: boolean
  modalStatusOpened: boolean
  modalRentalInfo: ModalRentalInfo
  modalStatusInfo: ModalStatusInfo

  openModalRental: (info: ModalRentalInfo) => void
  closeModalRental: () => void

  openModalStatus: (info: ModalStatusInfo) => void
  closeModalStatus: () => void

  modalAssignMoverOpened: boolean
  modalAssignMoverInfo: ModalAssignMoverInfo
  openModalAssignMover: (info: ModalAssignMoverInfo) => void
  closeModalAssignMover: () => void

  modalMoveToTruckOpened: boolean
  modalMoveToTruckInfo: ModalMoveToTruckInfo
  openModalMoveToTruck: (info: ModalMoveToTruckInfo) => void
  closeModalMoveToTruck: () => void

  movesConfirmedByDay: WorkDayResponse[]
  setMovesConfirmedByDay: (moves: WorkDayResponse[]) => void
}

export const useMoveManagerStore = create<MoveManagerStore>()(
  persist(
    (set) => ({
      // Seeded with the fleet default; the page re-points this at the selected
      // branch's own today as soon as the branch list resolves. Seeding from
      // the BROWSER's today put a dispatcher working another timezone — or
      // anyone near midnight — on the wrong day.
      date: todayInTimezone(DEFAULT_TIMEZONE),
      currentDay: toDateString(todayInTimezone(DEFAULT_TIMEZONE)),
      dateSelectedByUser: false,

      setDate: (date) => set({ date, currentDay: toDateString(date), dateSelectedByUser: true }),

      alignDateToTimezone: (timezone) =>
        set((state) => {
          if (state.dateSelectedByUser) return state

          const today = todayInTimezone(timezone)
          const day = toDateString(today)
          if (day === state.currentDay) return state

          return { date: today, currentDay: day }
        }),

      currentMainBranch: HOUSTON_BRANCH_ID,
      setCurrentMainBranch: (branch) => set({ currentMainBranch: branch }),

      modalRentalOpened: false,
      modalRentalInfo: { type: 'add', currentIdTruck: '', truckName: '' },

      openModalRental: (info) => set({ modalRentalOpened: true, modalRentalInfo: info }),
      closeModalRental: () => set({ modalRentalOpened: false }),

      modalStatusOpened: false,
      modalStatusInfo: { type: 'create', currentIdTrucks: [], truckName: '', currentNote: '' },

      openModalStatus: (info) => set({ modalStatusOpened: true, modalStatusInfo: info }),
      closeModalStatus: () => set({ modalStatusOpened: false }),

      modalAssignMoverOpened: false,
      modalAssignMoverInfo: { truckId: '', position: 0, target: 'truck' },
      openModalAssignMover: (info) => set({ modalAssignMoverOpened: true, modalAssignMoverInfo: info }),
      closeModalAssignMover: () => set({ modalAssignMoverOpened: false }),

      modalMoveToTruckOpened: false,
      modalMoveToTruckInfo: { operationId: '', customerNounce: '', currentTruckId: '' },
      openModalMoveToTruck: (info) => set({ modalMoveToTruckOpened: true, modalMoveToTruckInfo: info }),
      closeModalMoveToTruck: () => set({ modalMoveToTruckOpened: false }),

      movesConfirmedByDay: [],
      setMovesConfirmedByDay: (moves) => set({ movesConfirmedByDay: moves }),
    }),
    {
      name: 'move-manager-storage',
      partialize: (state) => ({
        currentMainBranch: state.currentMainBranch,
        date: state.date.toISOString(),
        currentDay: state.currentDay,
        dateSelectedByUser: state.dateSelectedByUser,
      }),
      onRehydrateStorage: () => (state) => {
        if (state && typeof state.date === 'string') {
          const rehydrated = new Date(state.date)
          state.date = new Date(rehydrated.getFullYear(), rehydrated.getMonth(), rehydrated.getDate())
        }
      },
    },
  ),
)
